/**
 * Pick the auth channel (phone+WhatsApp OTP vs email OTP) based on the
 * user's country. India → phone. Anywhere else → email. The user can
 * always override via the toggle on the login screen; the override is
 * persisted in localStorage so we don't re-detect on every visit.
 */

export type AuthChannel = 'phone' | 'email'

const CHANNEL_KEY = 'mellomap_auth_channel'
const COUNTRY_KEY = 'mellomap_country_code'

export function getStoredChannel(): AuthChannel | null {
  const v = localStorage.getItem(CHANNEL_KEY)
  return v === 'phone' || v === 'email' ? v : null
}

export function setStoredChannel(channel: AuthChannel) {
  localStorage.setItem(CHANNEL_KEY, channel)
}

export function getStoredCountry(): string | null {
  return localStorage.getItem(COUNTRY_KEY)
}

async function fetchCountryCode(): Promise<string | null> {
  // Primary: ipapi.co — free, no key, CORS-enabled, ~1000 req/day per IP.
  try {
    const res = await fetch('https://ipapi.co/json/', {
      headers: { Accept: 'application/json' },
    })
    if (res.ok) {
      const data = (await res.json()) as { country_code?: string }
      if (data.country_code) return data.country_code.toUpperCase()
    }
  } catch {
    /* fall through to backup */
  }

  // Backup: ipwho.is — free, no key.
  try {
    const res = await fetch('https://ipwho.is/', {
      headers: { Accept: 'application/json' },
    })
    if (res.ok) {
      const data = (await res.json()) as { country_code?: string; success?: boolean }
      if (data.success !== false && data.country_code) {
        return data.country_code.toUpperCase()
      }
    }
  } catch {
    /* give up */
  }

  return null
}

/**
 * Detect the auth channel for this visitor.
 * - If the user has previously picked one, honor it.
 * - Else look up country by IP, cache it, and map IN → phone / else → email.
 * - Returns null if detection failed and no stored channel — caller should
 *   render a manual "Are you in India?" chooser.
 */
export async function detectAuthChannel(): Promise<AuthChannel | null> {
  const stored = getStoredChannel()
  if (stored) return stored

  const cached = getStoredCountry()
  if (cached) {
    return cached === 'IN' ? 'phone' : 'email'
  }

  const country = await fetchCountryCode()
  if (!country) return null

  localStorage.setItem(COUNTRY_KEY, country)
  return country === 'IN' ? 'phone' : 'email'
}
